import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import api from "../../services/api";
import '../../assets/styles/onboarding.css';

const DOC_FIELD_CONFIG = [
  { field: "aadhaar", docType: "aadhaar", label: "Aadhaar Card", required: true, accept: ".pdf,.jpg,.jpeg,.png" },
  { field: "pan", docType: "pan", label: "PAN Card", required: true, accept: ".pdf,.jpg,.jpeg,.png" },
  { field: "photo", docType: "photo", label: "Passport Size Photo", required: true, accept: "image/jpeg,image/jpg,image/png" },
  { field: "marksheet10", docType: "marksheet_10", label: "10th Marksheet", required: true, accept: ".pdf,.jpg,.jpeg,.png" },
  { field: "marksheet12", docType: "marksheet_12", label: "12th Marksheet", required: true, accept: ".pdf,.jpg,.jpeg,.png" },
  { field: "passport", docType: "passport", label: "Passport", required: false, accept: ".pdf,.jpg,.jpeg,.png" },
  { field: "drivingLicense", docType: "driving_license", label: "Driving License", required: false, accept: ".pdf,.jpg,.jpeg,.png" },
  { field: "collegeId", docType: "college_id", label: "College ID", required: false, accept: ".pdf,.jpg,.jpeg,.png" }
];

function OnboardingFlow({ user, onSubmit, onLogout }) {
  const [step, setStep] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [sameAsCurrent, setSameAsCurrent] = useState(false);
  const containerRef = useRef(null);
  const [rejectedSections, setRejectedSections] = useState([]);
  const [resendReason, setResendReason] = useState("");
  const [resendCount, setResendCount] = useState(0);
  const [isResend, setIsResend] = useState(false);
  const [approvedHrOnboardingReq, setApprovedHrOnboardingReq] = useState(null);
  const [hrSignFile, setHrSignFile] = useState(null);
  const [hrSignComment, setHrSignComment] = useState("");
  const [instructionAccepted, setInstructionAccepted] = useState(false);
  const [showInstructionViewer, setShowInstructionViewer] = useState(false);
  const [existingDocs, setExistingDocs] = useState([]);
  const [lockedPrefill, setLockedPrefill] = useState({ fullName: false, email: false });

  const [d, setD] = useState({
    // Personal
    fullName: user.name || "",
    dob: "",
    bloodGroup: "",
    gender: "Male",
    maritalStatus: "Single",
    phone: "",
    email: "",
    
    // Address
    currentAddress: "",
    currentCity: "",
    currentState: "",
    currentPincode: "",
    permanentAddress: "",
    permanentCity: "",
    permanentState: "",
    permanentPincode: "",
    
    // Documents
    aadhaar: null, aadhaarName: "", aadhaarError: "",
    pan: null, panName: "", panError: "",
    drivingLicense: null, drivingLicenseName: "", drivingLicenseError: "",
    passport: null, passportName: "", passportError: "",
    collegeId: null, collegeIdName: "", collegeIdError: "",
    marksheet10: null, marksheet10Name: "",
    marksheet12: null, marksheet12Name: "",
    photo: null, photoName: "", photoError: "",
    certificates: [{ id: Date.now(), name: "", file: null, fileName: "", error: "" }],
    
    // Bank
    bankName: "",
    accountNo: "",
    ifsc: "",
    branch: "",
    
    // Education - Multiple Entries
    education: [
      {
        id: Date.now(),
        degree: "",
        specialization: "",
        institution: "",
        boardUniversity: "",
        score: "",
        scoreType: "percentage",
        startMonth: "",
        startYear: "",
        endMonth: "",
        endYear: "",
        isPursuing: false
      }
    ],
    
    // Joining Report
    firstName: "",
    lastName: "",
    fatherName: "",
    offerLetterDate: "",
    offerAcceptedDate: "",
    expectedJoiningDate: "",
    
    // Passport
    passportNumber: "",
    passportIssueDate: "",
    passportExpiryDate: "",
    passportIssuePlace: "",
    
    // PAN
    panNumber: "",
    
    // Driving License
    drivingLicenseNumber: "",
    drivingLicenseExpiry: "",
    
    // Declaration
    declaration: false,
    declarationDate: new Date().toISOString().split('T')[0],
    declarationPlace: ""
  });

  const validateFile = (file, field) => {
    const maxSize = 5 * 1024 * 1024;
    const allowedTypes = {
      aadhaar: ['.pdf', '.jpg', '.jpeg', '.png'],
      pan: ['.pdf', '.jpg', '.jpeg', '.png'],
      drivingLicense: ['.pdf', '.jpg', '.jpeg', '.png'],
      passport: ['.pdf', '.jpg', '.jpeg', '.png'],
      collegeId: ['.pdf', '.jpg', '.jpeg', '.png'],
      photo: ['.jpg', '.jpeg', '.png'],
      marksheet10: ['.pdf', '.jpg', '.jpeg', '.png'],
      marksheet12: ['.pdf', '.jpg', '.jpeg', '.png']
    };
    
    if (file.size > maxSize) return 'File size must be less than 5MB';
    
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes[field]?.includes(fileExt)) {
      return `Only ${allowedTypes[field].join(', ')} files are allowed`;
    }
    return null;
  };

  const u = useCallback((k, v) => setD(p => ({ ...p, [k]: v })), []);
  
  const uFile = useCallback((k, file) => {
    if (file) {
      const error = validateFile(file, k);
      if (error) {
        setD(p => ({ ...p, [k + 'Error']: error, [k]: null, [k + 'Name']: '' }));
        return;
      }
      setD(p => ({ ...p, [k]: file, [k + 'Name']: file.name, [k + 'Error']: '' }));
    }
  }, []);

  // Education handlers
  const addEducation = () => {
    setD(prev => ({
      ...prev,
      education: [
        ...prev.education,
        {
          id: Date.now(),
          degree: "",
          specialization: "",
          institution: "",
          boardUniversity: "",
          score: "",
          scoreType: "percentage",
          startMonth: "",
          startYear: "",
          endMonth: "",
          endYear: "",
          isPursuing: false
        }
      ]
    }));
  };

  const removeEducation = (id) => {
    setD(prev => ({
      ...prev,
      education: prev.education.filter(edu => edu.id !== id)
    }));
  };

  const updateEducation = (id, field, value) => {
    setD(prev => ({
      ...prev,
      education: prev.education.map(edu => 
        edu.id === id ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const addCertificate = () => {
    setD(prev => ({
      ...prev,
      certificates: [...(prev.certificates || []), { id: Date.now() + Math.floor(Math.random() * 1000), name: "", file: null, fileName: "", error: "" }]
    }));
  };

  const removeCertificate = (id) => {
    setD(prev => ({
      ...prev,
      certificates: (prev.certificates || []).filter(c => c.id !== id)
    }));
  };

  const updateCertificate = (id, field, value) => {
    setD(prev => ({
      ...prev,
      certificates: (prev.certificates || []).map(c => c.id === id ? { ...c, [field]: value } : c)
    }));
  };

  const updateCertificateFile = (id, file) => {
    if (!file) return;
    const error = validateFile(file, 'marksheet10');
    setD(prev => ({
      ...prev,
      certificates: (prev.certificates || []).map(c => c.id === id ? {
        ...c,
        file: error ? null : file,
        fileName: error ? "" : file.name,
        error: error || ""
      } : c)
    }));
  };

  useEffect(() => {
    if (containerRef.current) containerRef.current.scrollTop = 0;
  }, [step]);

  // =========================================
  // Load prefilled onboarding data (resend + HR uploaded basics)
  // =========================================
  useEffect(() => {
    async function loadPreviousData() {
      try {
        const status = await api.getOnboardingStatus();
        if (status) {
          setExistingDocs(Array.isArray(status.documents) ? status.documents : []);
          const profile = status.profile || {};
          const userInfo = status.user || {};
          setLockedPrefill({
            fullName: !!(profile.full_name || userInfo.name),
            email: !!(profile.personal_email || userInfo.email)
          });

          const prev = status.data || {};
          const prevEducation = (() => {
            if (!prev.education) return null;
            if (Array.isArray(prev.education)) return prev.education;
            if (typeof prev.education === "string") {
              try { return JSON.parse(prev.education); } catch { return null; }
            }
            return null;
          })();
          const normalizedEducation = Array.isArray(prevEducation) && prevEducation.length ? prevEducation.map((edu, idx) => ({
            id: edu.id || Date.now() + idx,
            degree: edu.degree || "",
            specialization: edu.specialization || "",
            institution: edu.institution || "",
            boardUniversity: edu.boardUniversity || edu.board_university || "",
            score: edu.score || "",
            scoreType: edu.scoreType || edu.score_type || "percentage",
            startMonth: edu.startMonth || edu.start_month || "",
            startYear: edu.startYear || edu.start_year || "",
            endMonth: edu.endMonth || edu.end_month || "",
            endYear: edu.endYear || edu.end_year || "",
            isPursuing: Boolean(edu.isPursuing ?? edu.is_pursuing)
          })) : null;

          setD(p => ({
            ...p,
            fullName: prev.fullName || prev.full_name || profile.full_name || userInfo.name || p.fullName,
            dob: prev.dob || profile.dob || p.dob,
            bloodGroup: prev.bloodGroup || prev.blood_group || profile.blood_group || p.bloodGroup,
            gender: prev.gender || profile.gender || p.gender,
            maritalStatus: prev.maritalStatus || prev.marital_status || profile.marital_status || p.maritalStatus,
            phone: prev.phone || profile.phone || p.phone,
            email: prev.email || prev.personal_email || profile.personal_email || userInfo.email || p.email,
            currentAddress: prev.currentAddress || prev.current_address || profile.current_address || p.currentAddress,
            currentCity: prev.currentCity || prev.current_city || profile.current_city || p.currentCity,
            currentState: prev.currentState || prev.current_state || profile.current_state || p.currentState,
            currentPincode: prev.currentPincode || prev.current_pincode || profile.current_pincode || p.currentPincode,
            permanentAddress: prev.permanentAddress || prev.permanent_address || profile.permanent_address || p.permanentAddress,
            permanentCity: prev.permanentCity || prev.permanent_city || profile.permanent_city || p.permanentCity,
            permanentState: prev.permanentState || prev.permanent_state || profile.permanent_state || p.permanentState,
            permanentPincode: prev.permanentPincode || prev.permanent_pincode || profile.permanent_pincode || p.permanentPincode,
            bankName: prev.bankName || prev.bank_name || status.bank_details?.bank_name || p.bankName,
            accountNo: prev.accountNo || prev.account_no || status.bank_details?.account_number || p.accountNo,
            ifsc: prev.ifsc || status.bank_details?.ifsc_code || p.ifsc,
            branch: prev.branch || status.bank_details?.branch || p.branch,
            firstName: prev.firstName || prev.first_name || profile.first_name || p.firstName,
            lastName: prev.lastName || prev.last_name || profile.last_name || p.lastName,
            fatherName: prev.fatherName || prev.father_name || profile.father_name || p.fatherName,
            offerLetterDate: prev.offerLetterDate || prev.offer_letter_date || profile.offer_letter_date || p.offerLetterDate,
            offerAcceptedDate: prev.offerAcceptedDate || prev.offer_accepted_date || profile.offer_accepted_date || p.offerAcceptedDate,
            expectedJoiningDate: prev.expectedJoiningDate || prev.expected_joining_date || profile.expected_joining_date || p.expectedJoiningDate,
            panNumber: prev.panNumber || prev.pan_number || profile.pan_number || p.panNumber,
            passportNumber: prev.passportNumber || prev.passport_number || profile.passport_number || p.passportNumber,
            passportIssueDate: prev.passportIssueDate || prev.passport_issue_date || profile.passport_issue_date || p.passportIssueDate,
            passportExpiryDate: prev.passportExpiryDate || prev.passport_expiry_date || profile.passport_expiry_date || p.passportExpiryDate,
            passportIssuePlace: prev.passportIssuePlace || prev.passport_issue_place || profile.passport_issue_place || p.passportIssuePlace,
            drivingLicenseNumber: prev.drivingLicenseNumber || prev.driving_license_number || profile.driving_license_number || p.drivingLicenseNumber,
            drivingLicenseExpiry: prev.drivingLicenseExpiry || prev.driving_license_expiry || profile.driving_license_expiry || p.drivingLicenseExpiry,
            education: normalizedEducation || p.education,
            declaration: false,
            declarationPlace: prev.declarationPlace || prev.declaration_place || status.declaration?.declaration_place || p.declarationPlace
          }));
        }

        if (status && status.resendCount > 0) {
          setIsResend(true);
          setResendCount(status.resendCount || 0);
          setResendReason(status.resendReason || '');
          const sections = status.rejectedSections;
          if (typeof sections === 'string') {
            try { setRejectedSections(JSON.parse(sections)); } catch { setRejectedSections([]); }
          } else if (Array.isArray(sections)) {
            setRejectedSections(sections);
          }
        }
      } catch (e) {
        console.warn('Could not load previous onboarding data:', e.message);
      }
    }
    loadPreviousData();
  }, []);

  useEffect(() => {
    async function loadHrOnboardingInstruction() {
      try {
        const row = await api.getActiveOnboardingInstruction();
        setApprovedHrOnboardingReq(row || null);
      } catch {
        setApprovedHrOnboardingReq(null);
      }
    }
    loadHrOnboardingInstruction();
  }, []);

  useEffect(() => {
    if (sameAsCurrent) {
      setD(prev => ({
        ...prev,
        permanentAddress: prev.currentAddress,
        permanentCity: prev.currentCity,
        permanentState: prev.currentState,
        permanentPincode: prev.currentPincode
      }));
    }
  }, [sameAsCurrent]);

  const getFieldError = (field) => {
    const value = d[field];
    switch(field) {
      case 'phone': 
        return value && !/^\d{10}$/.test(value) ? 'Enter valid 10-digit phone' : '';
      case 'email': 
        return value && !/\S+@\S+\.\S+/.test(value) ? 'Enter valid email' : '';
      case 'ifsc': 
        return value && !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value) ? 'Enter valid IFSC' : '';
      case 'currentPincode': case 'permanentPincode': 
        return value && !/^\d{6}$/.test(value) ? 'Enter valid 6-digit pincode' : '';
      case 'dob':
        if (value) {
          const age = new Date().getFullYear() - new Date(value).getFullYear();
          if (age < 18) return 'Must be 18 years or older';
          if (age > 100) return 'Invalid date of birth';
        }
        return '';
      case 'accountNo':
        return value && !/^\d{9,18}$/.test(value) ? 'Enter valid account number (9-18 digits)' : '';
      case 'panNumber':
        return value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value) ? 'Enter valid PAN (ABCDE1234F)' : '';
      case 'passportNumber':
        return value && !/^[A-Z]{1}[0-9]{7}$/.test(value) ? 'Enter valid passport number' : '';
      case 'drivingLicenseNumber':
        return value && !/^[A-Z]{2}[0-9]{13}$/.test(value) ? 'Enter valid driving license' : '';
      default: return '';
    }
  };

  const getInstructionDocUrl = useCallback(() => {
    if (!approvedHrOnboardingReq?.file_url) return "";
    return api.resolveFileUrl(approvedHrOnboardingReq.file_url);
  }, [approvedHrOnboardingReq]);

  const getInstructionDocType = useCallback(() => {
    const src = String(approvedHrOnboardingReq?.file_name || approvedHrOnboardingReq?.file_url || "").toLowerCase();
    if (src.endsWith(".pdf")) return "pdf";
    if (/\.(png|jpg|jpeg|webp|gif)(\?|$)/i.test(src)) return "image";
    return "other";
  }, [approvedHrOnboardingReq]);

  const steps = ["Personal & ID", "Address", "Documents", "Education", "Bank & Joining", "Review"];
  const hrBasicInfo = useMemo(() => ({
    department: user?.department || "",
    designation: user?.designation || ""
  }), [user?.department, user?.designation]);

  const latestDocByType = useMemo(() => {
    const map = {};
    (existingDocs || []).forEach((doc) => {
      const key = String(doc.doc_type || "").toLowerCase();
      if (!key) return;
      const existing = map[key];
      if (!existing) {
        map[key] = doc;
        return;
      }
      const oldTs = new Date(existing.uploaded_at || 0).getTime();
      const newTs = new Date(doc.uploaded_at || 0).getTime();
      if (newTs >= oldTs) map[key] = doc;
    });
    return map;
  }, [existingDocs]);

  const getDocWorkflowStatus = useCallback((field) => {
    const cfg = DOC_FIELD_CONFIG.find((c) => c.field === field);
    if (!cfg) return { state: "pending", label: "Pending", canUpload: true, doc: null };
    const doc = latestDocByType[cfg.docType] || null;
    const docStatus = String(doc?.status || "").toLowerCase();
    if (d[field]) return { state: "uploaded", label: "Uploaded (ready to submit)", canUpload: true, doc };
    if (!doc) return { state: "pending", label: "Pending", canUpload: true, doc: null };
    if (["approved", "available", "verified"].includes(docStatus)) {
      return { state: "verified", label: "Verified", canUpload: false, doc };
    }
    if (docStatus === "rejected") {
      return { state: "rejected", label: `Rejected${doc.review_comment ? ` - ${doc.review_comment}` : ""}`, canUpload: true, doc };
    }
    return { state: "uploaded", label: "Uploaded", canUpload: false, doc };
  }, [d, latestDocByType]);

  const canNext = useCallback(() => {
    // During resend, allow skipping non-rejected sections
    if (isResend && rejectedSections.length > 0 && !rejectedSections.includes(step)) {
      return true;
    }
    switch(step) {
      case 0:
        return d.fullName?.trim() && d.dob && !getFieldError('dob') &&
               !getFieldError('phone') && d.phone?.length === 10 &&
               !getFieldError('email') && d.email?.trim() && d.bloodGroup?.trim() &&
               !getFieldError('panNumber') && d.panNumber?.trim();
      case 1:
        const currentValid = d.currentAddress?.trim() && d.currentCity?.trim() && 
                           d.currentState?.trim() && !getFieldError('currentPincode') && 
                           d.currentPincode?.length === 6;
        const permanentValid = sameAsCurrent || (d.permanentAddress?.trim() && 
                          d.permanentCity?.trim() && d.permanentState?.trim() && 
                          !getFieldError('permanentPincode') && d.permanentPincode?.length === 6);
        return currentValid && permanentValid;
      case 2: 
        return ["aadhaar", "pan", "photo", "marksheet10", "marksheet12"].every((field) => {
          const st = getDocWorkflowStatus(field);
          return ["uploaded", "verified"].includes(st.state);
        }) && !d.aadhaarError && !d.panError && !d.photoError;
      case 3:
        return d.education.some(edu => edu.degree && edu.institution && edu.score);
      case 4:
        return d.bankName?.trim() && d.accountNo?.trim() && 
               !getFieldError('accountNo') && d.ifsc?.length === 11 &&
               !getFieldError('ifsc') && d.firstName?.trim() && d.lastName?.trim() &&
               d.fatherName?.trim() && d.offerLetterDate && d.offerAcceptedDate;
      case 5:
        if (approvedHrOnboardingReq && (!hrSignFile || !instructionAccepted)) return false;
        return d.declaration === true;
      default: return true;
    }
  }, [step, d, sameAsCurrent, approvedHrOnboardingReq, hrSignFile, instructionAccepted, isResend, rejectedSections, getDocWorkflowStatus]);

  // =========================================
  // FIXED doSubmit - MATCHES BACKEND EXACTLY
  // =========================================
  const doSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const fd = new FormData();
      
      // ========== PERSONAL & ID ==========
      fd.append('fullName', d.fullName || '');
      fd.append('dob', d.dob || '');
      fd.append('bloodGroup', d.bloodGroup || '');
      fd.append('gender', d.gender || 'Male');
      fd.append('maritalStatus', d.maritalStatus || 'Single');
      fd.append('phone', d.phone || '');
      fd.append('email', d.email || '');
      fd.append('panNumber', d.panNumber || '');
      
      // Passport (optional)
      if (d.passportNumber) fd.append('passportNumber', d.passportNumber);
      if (d.passportIssueDate) fd.append('passportIssueDate', d.passportIssueDate);
      if (d.passportExpiryDate) fd.append('passportExpiryDate', d.passportExpiryDate);
      if (d.passportIssuePlace) fd.append('passportIssuePlace', d.passportIssuePlace);
      
      // Driving License (optional)
      if (d.drivingLicenseNumber) fd.append('drivingLicenseNumber', d.drivingLicenseNumber);
      if (d.drivingLicenseExpiry) fd.append('drivingLicenseExpiry', d.drivingLicenseExpiry);
      
      // ========== ADDRESS ==========
      fd.append('currentAddress', d.currentAddress || '');
      fd.append('currentCity', d.currentCity || '');
      fd.append('currentState', d.currentState || '');
      fd.append('currentPincode', d.currentPincode || '');
      
      if (sameAsCurrent) {
        fd.append('permanentAddress', d.currentAddress || '');
        fd.append('permanentCity', d.currentCity || '');
        fd.append('permanentState', d.currentState || '');
        fd.append('permanentPincode', d.currentPincode || '');
      } else {
        fd.append('permanentAddress', d.permanentAddress || '');
        fd.append('permanentCity', d.permanentCity || '');
        fd.append('permanentState', d.permanentState || '');
        fd.append('permanentPincode', d.permanentPincode || '');
      }
      
      // ========== BANK ==========
      fd.append('bankName', d.bankName || '');
      fd.append('accountNo', d.accountNo || '');
      fd.append('ifsc', d.ifsc || '');
      fd.append('branch', d.branch || '');
      
      // ========== JOINING REPORT ==========
      fd.append('firstName', d.firstName || '');
      fd.append('lastName', d.lastName || '');
      fd.append('fatherName', d.fatherName || '');
      fd.append('offerLetterDate', d.offerLetterDate || '');
      fd.append('offerAcceptedDate', d.offerAcceptedDate || '');
      if (d.expectedJoiningDate) fd.append('expectedJoiningDate', d.expectedJoiningDate);
      
      // ========== EDUCATION ==========
      fd.append('education', JSON.stringify(d.education));
      
      // ========== DECLARATION ==========
      fd.append('declaration', d.declaration ? 'true' : 'false');
      fd.append('declarationPlace', d.declarationPlace || '');
      fd.append('declarationDate', d.declarationDate || new Date().toISOString().split('T')[0]);
      
      // ========== FILE UPLOADS - CRITICAL: Must match route EXACTLY ==========
      // Required Documents
      if (d.aadhaar) fd.append("aadhaar", d.aadhaar);
      if (d.pan) fd.append("pan", d.pan);
      if (d.photo) fd.append("photo", d.photo);
      
      // Optional Documents - Use EXACT field names from route
      if (d.drivingLicense) fd.append("drivingLicense", d.drivingLicense);
      if (d.passport) fd.append("passport", d.passport);
      if (d.collegeId) fd.append("collegeId", d.collegeId);
      if (d.marksheet10) fd.append("marksheet10", d.marksheet10);
      if (d.marksheet12) fd.append("marksheet12", d.marksheet12);
      const certMeta = (d.certificates || [])
        .filter(c => c && c.name && c.file)
        .map(c => ({ id: c.id, name: c.name, fileName: c.fileName || c.file?.name || '' }));
      if (certMeta.length) fd.append("certificatesMeta", JSON.stringify(certMeta));
      (d.certificates || []).forEach((c) => {
        if (c?.name && c?.file) fd.append("certificates", c.file);
      });
      if (approvedHrOnboardingReq && hrSignFile) {
        if (approvedHrOnboardingReq.req_id) fd.append("hrOnboardingRequestId", approvedHrOnboardingReq.req_id);
        fd.append("hrOnboardingInstructionAccepted", instructionAccepted ? 'true' : 'false');
        fd.append("hrOnboardingSignatureComment", hrSignComment || '');
        fd.append("hrOnboardingSignature", hrSignFile);
      }

      // Debug log
      console.log("📤 Submitting onboarding with fields:");
      for (let pair of fd.entries()) {
        if (pair[0].includes('aadhaar') || pair[0].includes('pan') || pair[0].includes('photo') ||
            pair[0].includes('driving') || pair[0].includes('passport') || pair[0].includes('marksheet') || pair[0].includes('certificate')) {
          console.log(`  ${pair[0]}: [FILE] ${pair[1].name}`);
        } else {
          console.log(`  ${pair[0]}: ${pair[1]}`);
        }
      }
      
      const response = await api.submitOnboarding(fd);
      console.log("✅ Onboarding submission response:", response);
      
      if (response.success || response.status === 'ONBOARDING_SUBMITTED') {
        localStorage.removeItem('onboardingDraft');
        onSubmit(); // Navigate to PendingVerification screen
      }
    } catch (e) {
      console.error("❌ Submission error:", e);
      setSubmitError(e.message || "Submission failed. Please check all required fields and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const FileStatus = ({ fileName, error }) => {
    if (error) return <div className="ob-file-status ob-file-error">❌ {error}</div>;
    if (fileName) return <div className="ob-file-status ob-file-success">✅ {fileName}</div>;
    return null;
  };

  return (
    <div className="ob-root">
      <div className="ob-hdr">
        <div className="ob-hdr-brand">
          <img src="/logo.png" alt="AMS" className="ob-hdr-logo" />
          <div>
            <div className="ob-hdr-title">AMSHRMS — Onboarding</div>
            <div className="ob-hdr-sub">Complete your profile to continue · {user.id}</div>
          </div>
        </div>
        <button className="ob-logout" onClick={onLogout} aria-label="Sign out">Sign Out</button>
      </div>

      <div className="ob-body">
        <div className="ob-container" ref={containerRef}>
          <div className="ob-steps">
            {steps.map((s, i) => (
              <div
                key={i}
                className={"ob-step" + (i === step ? " active" : i < step ? " done" : "")}
                onClick={() => i <= step && setStep(i)}
                role="button"
                tabIndex={0}
                aria-label={`Step ${i + 1}: ${s}`}
              >
                {i < step ? "✓" : i + 1}
                <div style={{fontSize: '10px', marginTop: '2px'}}>{s}</div>
              </div>
            ))}
          </div>

          {/* RESEND BANNER */}
          {isResend && (
            <div style={{
              background: 'linear-gradient(135deg, #fef3c7, #fffbeb)',
              border: '1.5px solid #f59e0b',
              borderRadius: 12, padding: '16px 20px', marginBottom: 16
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <span style={{ fontWeight: 700, fontSize: 14, color: '#92400e' }}>
                  Onboarding Resent (Attempt #{resendCount + 1})
                </span>
              </div>
              {resendReason && (
                <div style={{ fontSize: 13, color: '#78350f', marginBottom: 8, lineHeight: 1.5 }}>
                  <strong>HR Reason:</strong> {resendReason}
                </div>
              )}
              {rejectedSections.length > 0 && (
                <div style={{ fontSize: 12, color: '#92400e' }}>
                  <strong>Sections to fix:</strong>{' '}
                  {rejectedSections.map((s, i) => (
                    <span key={i} style={{
                      display: 'inline-flex', padding: '2px 8px', borderRadius: 12,
                      background: '#fbbf24', color: '#78350f', fontSize: 11,
                      fontWeight: 600, margin: '2px 4px 2px 0'
                    }}>
                      {['Personal & ID', 'Address', 'Documents', 'Education', 'Bank & Joining', 'Declaration'][s] || `Section ${s}`}
                    </span>
                  ))}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#a16207', marginTop: 8 }}>
                Previously filled data has been preserved. Please fix the highlighted sections and resubmit.
              </div>
            </div>
          )}

          {/* STEP 0: Personal & ID Details */}
          {!showPreview && step === 0 && (
            <div className="ob-card" style={isResend && rejectedSections.length > 0 && !rejectedSections.includes(0) ? { opacity: 0.6, pointerEvents: 'none', position: 'relative' } : isResend && rejectedSections.includes(0) ? { border: '2px solid #f59e0b' } : {}}>
              {isResend && rejectedSections.length > 0 && !rejectedSections.includes(0) && (
                <div style={{ position: 'absolute', top: 8, right: 12, fontSize: 10, fontWeight: 700, color: '#3b82f6', background: '#eff6ff', padding: '2px 8px', borderRadius: 10, zIndex: 2 }}>✓ No changes needed</div>
              )}
              <div className="ob-card-title">Personal & Identity Details</div>
              
              {/* Basic Personal */}
              <div className="ob-section">
                <div className="ob-section-title">👤 Personal Information</div>
                {(lockedPrefill.fullName || lockedPrefill.email || hrBasicInfo.department || hrBasicInfo.designation) && (
                  <div style={{ marginBottom: 12, padding: 10, borderRadius: 8, background: "#eff6ff", border: "1px solid #bfdbfe", fontSize: 12, color: "#1e3a8a" }}>
                    HR pre-filled basic details are read-only.
                    {hrBasicInfo.department ? <div style={{ marginTop: 4 }}><b>Department:</b> {hrBasicInfo.department}</div> : null}
                    {hrBasicInfo.designation ? <div style={{ marginTop: 2 }}><b>Designation:</b> {hrBasicInfo.designation}</div> : null}
                  </div>
                )}
                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">Full Name <span className="required">*</span></label>
                    <input className="ob-fi" value={d.fullName} onChange={e => u("fullName", e.target.value)} placeholder="Full legal name" disabled={lockedPrefill.fullName} />
                  </div>
                  <div className={`ob-fg ${getFieldError('dob') ? 'has-error' : ''}`}>
                    <label className="ob-fl">Date of Birth <span className="required">*</span></label>
                    <input className="ob-fi" type="date" value={d.dob} onChange={e => u("dob", e.target.value)} max={new Date().toISOString().split('T')[0]} />
                    {getFieldError('dob') && <div className="ob-field-error">{getFieldError('dob')}</div>}
                  </div>
                </div>
                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">Blood Group <span className="required">*</span></label>
                    <select className="ob-sel" value={d.bloodGroup} onChange={e => u("bloodGroup", e.target.value)}>
                      <option value="">Select</option>
                      <option>A+</option><option>A-</option><option>B+</option><option>B-</option>
                      <option>O+</option><option>O-</option><option>AB+</option><option>AB-</option>
                    </select>
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">Gender</label>
                    <select className="ob-sel" value={d.gender} onChange={e => u("gender", e.target.value)}>
                      <option>Male</option><option>Female</option><option>Other</option>
                    </select>
                  </div>
                </div>
                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">Marital Status</label>
                    <select className="ob-sel" value={d.maritalStatus} onChange={e => u("maritalStatus", e.target.value)}>
                      <option>Single</option><option>Married</option><option>Divorced</option>
                    </select>
                  </div>
                  <div className={`ob-fg ${getFieldError('phone') ? 'has-error' : ''}`}>
                    <label className="ob-fl">Phone <span className="required">*</span></label>
                    <input className="ob-fi" value={d.phone} onChange={e => u("phone", e.target.value.replace(/\D/g,''))} placeholder="9876543210" maxLength={10} />
                    {getFieldError('phone') && <div className="ob-field-error">{getFieldError('phone')}</div>}
                  </div>
                </div>
                <div className="ob-row">
                  <div className={`ob-fg ${getFieldError('email') ? 'has-error' : ''} ob-span-2`}>
                    <label className="ob-fl">Email <span className="required">*</span></label>
                    <input className="ob-fi" type="email" value={d.email} onChange={e => u("email", e.target.value)} placeholder="your@email.com" disabled={lockedPrefill.email} />
                    {getFieldError('email') && <div className="ob-field-error">{getFieldError('email')}</div>}
                  </div>
                </div>
              </div>

              {/* PAN Details */}
              <div className="ob-section">
                <div className="ob-section-title">🆔 PAN Card Details</div>
                <div className="ob-row">
                  <div className={`ob-fg ${getFieldError('panNumber') ? 'has-error' : ''}`}>
                    <label className="ob-fl">PAN Number <span className="required">*</span></label>
                    <input className="ob-fi" value={d.panNumber} onChange={e => u("panNumber", e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} />
                    {getFieldError('panNumber') && <div className="ob-field-error">{getFieldError('panNumber')}</div>}
                  </div>
                </div>
              </div>

              {/* Passport (Optional) */}
              <div className="ob-section">
                <div className="ob-section-title">🛂 Passport Details (Optional)</div>
                <div className="ob-row">
                  <div className={`ob-fg ${getFieldError('passportNumber') ? 'has-error' : ''}`}>
                    <label className="ob-fl">Passport Number</label>
                    <input className="ob-fi" value={d.passportNumber} onChange={e => u("passportNumber", e.target.value.toUpperCase())} placeholder="A1234567" maxLength={8} />
                    {getFieldError('passportNumber') && <div className="ob-field-error">{getFieldError('passportNumber')}</div>}
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">Issue Date</label>
                    <input className="ob-fi" type="date" value={d.passportIssueDate} onChange={e => u("passportIssueDate", e.target.value)} />
                  </div>
                </div>
                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">Expiry Date</label>
                    <input className="ob-fi" type="date" value={d.passportExpiryDate} onChange={e => u("passportExpiryDate", e.target.value)} />
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">Place of Issue</label>
                    <input className="ob-fi" value={d.passportIssuePlace} onChange={e => u("passportIssuePlace", e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Driving License (Optional) */}
              <div className="ob-section">
                <div className="ob-section-title">🚗 Driving License (Optional)</div>
                <div className="ob-row">
                  <div className={`ob-fg ${getFieldError('drivingLicenseNumber') ? 'has-error' : ''}`}>
                    <label className="ob-fl">License Number</label>
                    <input className="ob-fi" value={d.drivingLicenseNumber} onChange={e => u("drivingLicenseNumber", e.target.value.toUpperCase())} />
                    {getFieldError('drivingLicenseNumber') && <div className="ob-field-error">{getFieldError('drivingLicenseNumber')}</div>}
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">Expiry Date</label>
                    <input className="ob-fi" type="date" value={d.drivingLicenseExpiry} onChange={e => u("drivingLicenseExpiry", e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 1: Address */}
          {!showPreview && step === 1 && (
            <div className="ob-card">
              <div className="ob-card-title">Address Details</div>
              
              <div className="ob-section">
                <div className="ob-section-title">🏠 Current Address</div>
                <div className="ob-row">
                  <div className="ob-fg ob-span-2">
                    <label className="ob-fl">Address <span className="required">*</span></label>
                    <textarea className="ob-fi ob-textarea" value={d.currentAddress} onChange={e => u("currentAddress", e.target.value)} placeholder="Street address, landmark" />
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">City <span className="required">*</span></label>
                    <input className="ob-fi" value={d.currentCity} onChange={e => u("currentCity", e.target.value)} />
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">State <span className="required">*</span></label>
                    <input className="ob-fi" value={d.currentState} onChange={e => u("currentState", e.target.value)} />
                  </div>
                  <div className={`ob-fg ${getFieldError('currentPincode') ? 'has-error' : ''}`}>
                    <label className="ob-fl">Pincode <span className="required">*</span></label>
                    <input className="ob-fi" value={d.currentPincode} onChange={e => u("currentPincode", e.target.value.replace(/\D/g,''))} placeholder="400001" maxLength={6} />
                    {getFieldError('currentPincode') && <div className="ob-field-error">{getFieldError('currentPincode')}</div>}
                  </div>
                </div>
              </div>

              <label className="ob-checkbox-label">
                <input type="checkbox" checked={sameAsCurrent} onChange={e => setSameAsCurrent(e.target.checked)} className="ob-checkbox" />
                <span className="ob-checkbox-custom"></span>
                Permanent address same as current address
              </label>

              <div className="ob-section">
                <div className="ob-section-title">🏠 Permanent Address</div>
                <div className="ob-row">
                  <div className="ob-fg ob-span-2">
                    <label className="ob-fl">Address <span className="required">*</span></label>
                    <textarea className="ob-fi ob-textarea" value={d.permanentAddress} onChange={e => u("permanentAddress", e.target.value)} placeholder="Street address, landmark" disabled={sameAsCurrent} />
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">City <span className="required">*</span></label>
                    <input className="ob-fi" value={d.permanentCity} onChange={e => u("permanentCity", e.target.value)} disabled={sameAsCurrent} />
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">State <span className="required">*</span></label>
                    <input className="ob-fi" value={d.permanentState} onChange={e => u("permanentState", e.target.value)} disabled={sameAsCurrent} />
                  </div>
                  <div className={`ob-fg ${getFieldError('permanentPincode') ? 'has-error' : ''}`}>
                    <label className="ob-fl">Pincode <span className="required">*</span></label>
                    <input className="ob-fi" value={d.permanentPincode} onChange={e => u("permanentPincode", e.target.value.replace(/\D/g,''))} placeholder="400001" maxLength={6} disabled={sameAsCurrent} />
                    {getFieldError('permanentPincode') && !sameAsCurrent && <div className="ob-field-error">{getFieldError('permanentPincode')}</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2: Documents */}
          {!showPreview && step === 2 && (
            <div className="ob-card">
              <div className="ob-card-title">Document Upload Dashboard</div>
              <div style={{fontSize: '13px', color: '#64748b', marginBottom: '20px'}}>
                Status flow: Pending / Uploaded / Verified / Rejected. Upload is enabled only for Pending or Rejected.
              </div>

              <div className="ob-section">
                <div className="ob-section-title">Required Documents</div>
                {DOC_FIELD_CONFIG.filter((c) => c.required).map((cfg) => {
                  const st = getDocWorkflowStatus(cfg.field);
                  const statusColor = st.state === "verified" ? "#2563eb" : st.state === "rejected" ? "#dc2626" : st.state === "uploaded" ? "#d97706" : "#64748b";
                  return (
                    <div className="ob-row" key={cfg.field}>
                      <div className="ob-fg ob-span-2" style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
                        <label className="ob-fl">{cfg.label} <span className="required">*</span></label>
                        <div style={{ fontSize: 12, color: statusColor, marginBottom: 8 }}><b>Status:</b> {st.label}</div>
                        {st.doc?.id ? (
                          <button type="button" className="ob-btn-o" style={{ marginBottom: 8 }} onClick={async () => { try { await api.previewDocById(st.doc.id); } catch (e) { alert("Preview failed: " + e.message); } }}>
                            Preview Uploaded File
                          </button>
                        ) : null}
                        <input className="ob-fi ob-fi-file" type="file" accept={cfg.accept} onChange={e => uFile(cfg.field, e.target.files[0])} disabled={!st.canUpload} />
                        <FileStatus fileName={d[cfg.field + "Name"]} error={d[cfg.field + "Error"]} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="ob-section">
                <div className="ob-section-title">Optional Documents</div>
                {DOC_FIELD_CONFIG.filter((c) => !c.required).map((cfg) => {
                  const st = getDocWorkflowStatus(cfg.field);
                  const statusColor = st.state === "verified" ? "#2563eb" : st.state === "rejected" ? "#dc2626" : st.state === "uploaded" ? "#d97706" : "#64748b";
                  return (
                    <div className="ob-row" key={cfg.field}>
                      <div className="ob-fg ob-span-2" style={{ border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 }}>
                        <label className="ob-fl">{cfg.label}</label>
                        <div style={{ fontSize: 12, color: statusColor, marginBottom: 8 }}><b>Status:</b> {st.label}</div>
                        {st.doc?.id ? (
                          <button type="button" className="ob-btn-o" style={{ marginBottom: 8 }} onClick={async () => { try { await api.previewDocById(st.doc.id); } catch (e) { alert("Preview failed: " + e.message); } }}>
                            Preview Uploaded File
                          </button>
                        ) : null}
                        <input className="ob-fi ob-fi-file" type="file" accept={cfg.accept} onChange={e => uFile(cfg.field, e.target.files[0])} disabled={!st.canUpload} />
                        <FileStatus fileName={d[cfg.field + "Name"]} error={d[cfg.field + "Error"]} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="ob-section">
                <div className="ob-section-title">Certificates (Optional)</div>
                {(d.certificates || []).map((cert, idx) => (
                  <div key={cert.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 12, marginBottom: 10 }}>
                    <div className="ob-row">
                      <div className="ob-fg">
                        <label className="ob-fl">Certificate Name</label>
                        <input className="ob-fi" value={cert.name} onChange={e => updateCertificate(cert.id, "name", e.target.value)} placeholder={`Certificate ${idx + 1}`} />
                      </div>
                      <div className="ob-fg">
                        <label className="ob-fl">Upload Certificate</label>
                        <input className="ob-fi ob-fi-file" type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e => updateCertificateFile(cert.id, e.target.files?.[0])} />
                        {cert.error ? <div className="ob-file-status ob-file-error">Invalid file: {cert.error}</div> : (cert.fileName ? <div className="ob-file-status">Uploaded: {cert.fileName}</div> : null)}
                      </div>
                    </div>
                    {(d.certificates || []).length > 1 && (
                      <button type="button" onClick={() => removeCertificate(cert.id)} style={{ border: '1px solid #fecaca', background: '#fff1f2', color: '#dc2626', padding: '4px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer' }}>
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="ob-btn-o" onClick={addCertificate}>+ Add Certificate</button>
              </div>
            </div>
          )}

          {/* STEP 3: Education */}
          {!showPreview && step === 3 && (
            <div className="ob-card">
              <div className="ob-card-title">Education Details</div>
              <div style={{fontSize: '13px', color: '#64748b', marginBottom: '20px'}}>
                Add your educational qualifications (Minimum one entry required)
              </div>

              {d.education.map((edu, index) => (
                <div key={edu.id} className="ob-section" style={{border: '1px solid #e2e8f0', padding: '16px', borderRadius: '12px', marginBottom: '16px'}}>
                  <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px'}}>
                    <div className="ob-section-title" style={{marginBottom: 0}}>Education #{index + 1}</div>
                    {d.education.length > 1 && (
                      <button 
                        type="button" 
                        onClick={() => removeEducation(edu.id)}
                        style={{border: 'none', background: '#fee2e2', color: '#ef4444', padding: '4px 12px', borderRadius: '6px', fontSize: '11px', fontWeight: '600', cursor: 'pointer'}}
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="ob-row">
                    <div className="ob-fg">
                      <label className="ob-fl">Degree <span className="required">*</span></label>
                      <input className="ob-fi" value={edu.degree} onChange={e => updateEducation(edu.id, "degree", e.target.value)} placeholder="B.Tech, M.Sc, etc." />
                    </div>
                    <div className="ob-fg">
                      <label className="ob-fl">Specialization <span className="required">*</span></label>
                      <input className="ob-fi" value={edu.specialization} onChange={e => updateEducation(edu.id, "specialization", e.target.value)} placeholder="Computer Science, etc." />
                    </div>
                  </div>

                  <div className="ob-row">
                    <div className="ob-fg">
                      <label className="ob-fl">Institution/College <span className="required">*</span></label>
                      <input className="ob-fi" value={edu.institution} onChange={e => updateEducation(edu.id, "institution", e.target.value)} />
                    </div>
                    <div className="ob-fg">
                      <label className="ob-fl">Board/University</label>
                      <input className="ob-fi" value={edu.boardUniversity} onChange={e => updateEducation(edu.id, "boardUniversity", e.target.value)} />
                    </div>
                  </div>

                  <div className="ob-row">
                    <div className="ob-fg">
                      <label className="ob-fl">Start Month</label>
                      <select className="ob-sel" value={edu.startMonth} onChange={e => updateEducation(edu.id, "startMonth", e.target.value)}>
                        <option value="">Select</option>
                        <option>January</option><option>February</option><option>March</option><option>April</option>
                        <option>May</option><option>June</option><option>July</option><option>August</option>
                        <option>September</option><option>October</option><option>November</option><option>December</option>
                      </select>
                    </div>
                    <div className="ob-fg">
                      <label className="ob-fl">Start Year</label>
                      <input className="ob-fi" value={edu.startYear} onChange={e => updateEducation(edu.id, "startYear", e.target.value.replace(/\D/g,''))} placeholder="2020" maxLength={4} />
                    </div>
                  </div>

                  <div className="ob-row">
                    <div className="ob-fg">
                      <label className="ob-fl">End Month</label>
                      <select className="ob-sel" value={edu.endMonth} onChange={e => updateEducation(edu.id, "endMonth", e.target.value)} disabled={edu.isPursuing}>
                        <option value="">Select</option>
                        <option>January</option><option>February</option><option>March</option><option>April</option>
                        <option>May</option><option>June</option><option>July</option><option>August</option>
                        <option>September</option><option>October</option><option>November</option><option>December</option>
                      </select>
                    </div>
                    <div className="ob-fg">
                      <label className="ob-fl">End Year</label>
                      <input className="ob-fi" value={edu.endYear} onChange={e => updateEducation(edu.id, "endYear", e.target.value.replace(/\D/g,''))} placeholder="2024" maxLength={4} disabled={edu.isPursuing} />
                    </div>
                  </div>

                  <div className="ob-checkbox-label" style={{marginTop: '8px'}}>
                    <input 
                      type="checkbox" 
                      checked={edu.isPursuing} 
                      onChange={e => updateEducation(edu.id, "isPursuing", e.target.checked)} 
                      className="ob-checkbox" 
                    />
                    <span className="ob-checkbox-custom"></span>
                    I am currently pursuing this degree
                  </div>

                  <div className="ob-row" style={{marginTop: '12px'}}>
                    <div className="ob-fg">
                      <label className="ob-fl">Score <span className="required">*</span></label>
                      <input className="ob-fi" value={edu.score} onChange={e => updateEducation(edu.id, "score", e.target.value)} placeholder="85.5" />
                    </div>
                    <div className="ob-fg">
                      <label className="ob-fl">Score Type</label>
                      <select className="ob-sel" value={edu.scoreType} onChange={e => updateEducation(edu.id, "scoreType", e.target.value)}>
                        <option value="percentage">Percentage (%)</option>
                        <option value="cgpa">CGPA</option>
                        <option value="gpa">GPA</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}

              <button 
                className="ob-btn-o" 
                onClick={addEducation}
                style={{width: '100%', marginTop: '8px'}}
              >
                + Add Another Education
              </button>
            </div>
          )}

          {/* STEP 4: Bank & Joining */}
          {!showPreview && step === 4 && (
            <div className="ob-card">
              <div className="ob-card-title">Bank & Joining Details</div>
              
              {/* Bank Details */}
              <div className="ob-section">
                <div className="ob-section-title">🏦 Bank Account Details</div>
                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">Bank Name <span className="required">*</span></label>
                    <input className="ob-fi" value={d.bankName} onChange={e => u("bankName", e.target.value)} />
                  </div>
                  <div className={`ob-fg ${getFieldError('accountNo') ? 'has-error' : ''}`}>
                    <label className="ob-fl">Account Number <span className="required">*</span></label>
                    <input className="ob-fi" value={d.accountNo} onChange={e => u("accountNo", e.target.value.replace(/\D/g,''))} maxLength={18} />
                    {getFieldError('accountNo') && <div className="ob-field-error">{getFieldError('accountNo')}</div>}
                  </div>
                </div>
                <div className="ob-row">
                  <div className={`ob-fg ${getFieldError('ifsc') ? 'has-error' : ''}`}>
                    <label className="ob-fl">IFSC Code <span className="required">*</span></label>
                    <input className="ob-fi" value={d.ifsc} onChange={e => u("ifsc", e.target.value.toUpperCase())} placeholder="SBIN0001234" maxLength={11} />
                    {getFieldError('ifsc') && <div className="ob-field-error">{getFieldError('ifsc')}</div>}
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">Branch</label>
                    <input className="ob-fi" value={d.branch} onChange={e => u("branch", e.target.value)} />
                  </div>
                </div>
              </div>

              {/* Joining Report */}
              <div className="ob-section">
                <div className="ob-section-title">📋 Joining Report</div>
                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">First Name <span className="required">*</span></label>
                    <input className="ob-fi" value={d.firstName} onChange={e => u("firstName", e.target.value)} />
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">Last Name <span className="required">*</span></label>
                    <input className="ob-fi" value={d.lastName} onChange={e => u("lastName", e.target.value)} />
                  </div>
                </div>
                <div className="ob-row">
                  <div className="ob-fg ob-span-2">
                    <label className="ob-fl">Father's Name <span className="required">*</span></label>
                    <input className="ob-fi" value={d.fatherName} onChange={e => u("fatherName", e.target.value)} />
                  </div>
                </div>
                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">Offer Letter Date <span className="required">*</span></label>
                    <input className="ob-fi" type="date" value={d.offerLetterDate} onChange={e => u("offerLetterDate", e.target.value)} />
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">Offer Accepted Date <span className="required">*</span></label>
                    <input className="ob-fi" type="date" value={d.offerAcceptedDate} onChange={e => u("offerAcceptedDate", e.target.value)} />
                  </div>
                </div>
                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">Expected Joining Date</label>
                    <input className="ob-fi" type="date" value={d.expectedJoiningDate} onChange={e => u("expectedJoiningDate", e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: Review & Declaration */}
          {!showPreview && step === 5 && (
            <div className="ob-card">
              <div className="ob-card-title">Review & Declaration</div>

              {approvedHrOnboardingReq && (
                <div className="ob-section">
                  <div className="ob-section-title">HR Approved Onboarding File (Read Before Acknowledgement)</div>
                  <div style={{
                    background: '#eff6ff',
                    border: '1.5px solid #bfdbfe',
                    borderRadius: 12,
                    padding: '14px 16px',
                    marginBottom: 12
                  }}>
                    <div style={{fontSize:'12px', fontWeight:700, color:'#1d4ed8', marginBottom:6}}>
                      {approvedHrOnboardingReq.req_id ? `Request: ${approvedHrOnboardingReq.req_id} | ` : ''}Status: {approvedHrOnboardingReq.status || 'active'}
                    </div>
                    <div style={{fontSize:'12px', color:'#334155', marginBottom:8}}>
                      {approvedHrOnboardingReq.pm_comment || approvedHrOnboardingReq.hr_comment || 'Please read this file fully and upload your digital signature copy.'}
                    </div>
                    <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                      <button type="button" className="ob-btn-o" onClick={() => setShowInstructionViewer(true)}>View File</button>
                    </div>
                  </div>
                  <div className="ob-row">
                    <div className="ob-fg">
                      <label className="ob-fl">Upload Digital Signature Photo Copy <span className="required">*</span></label>
                      <input type="file" className="ob-fi" onChange={e => setHrSignFile(e.target.files?.[0] || null)} />
                      {!hrSignFile && <div className="ob-field-error">Digital signature file required before acknowledgement</div>}
                    </div>
                  </div>
                  <div className="ob-row">
                    <div className="ob-fg">
                      <label className="ob-fl">Signature Comment (optional)</label>
                      <textarea className="ob-fi" rows={2} value={hrSignComment} onChange={e => setHrSignComment(e.target.value)} />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Declaration */}
              <div className="ob-section">
                <div className="ob-section-title">📜 Acknowledgement & Declaration</div>
                <div style={{
                  background: '#f8fafc',
                  border: '1.5px solid #e2e8f0',
                  borderRadius: '12px',
                  padding: '20px',
                  marginBottom: '20px',
                  fontSize: '13px',
                  lineHeight: '1.6',
                  color: '#334155'
                }}>
                  <p style={{marginTop: 0, fontWeight: 600}}>I hereby declare that:</p>
                  <ul style={{paddingLeft: '20px', marginBottom: '16px'}}>
                    <li>All the information provided by me in this application is true, correct, and complete to the best of my knowledge and belief.</li>
                    <li>I understand that any false information or suppression of facts may lead to disqualification or termination of employment.</li>
                    <li>I authorize AMSHRMS to verify all the information and documents provided by me.</li>
                    <li>I have read and understood the terms and conditions of employment.</li>
                  </ul>
                </div>

                <div className="ob-row">
                  <div className="ob-fg">
                    <label className="ob-fl">Place <span className="required">*</span></label>
                    <input 
                      className="ob-fi" 
                      value={d.declarationPlace} 
                      onChange={e => u("declarationPlace", e.target.value)} 
                      placeholder="City" 
                    />
                  </div>
                  <div className="ob-fg">
                    <label className="ob-fl">Date</label>
                    <input 
                      className="ob-fi" 
                      type="date" 
                      value={d.declarationDate} 
                      onChange={e => u("declarationDate", e.target.value)} 
                      disabled 
                    />
                  </div>
                </div>

                <label className="ob-checkbox-label" style={{marginTop: '16px', padding: '12px', background: '#f1f5f9', borderRadius: '8px'}}>
                  <input 
                    type="checkbox" 
                    checked={d.declaration} 
                    onChange={e => u("declaration", e.target.checked)} 
                    disabled={!!approvedHrOnboardingReq && !instructionAccepted}
                    className="ob-checkbox" 
                  />
                  <span className="ob-checkbox-custom"></span>
                  <span style={{fontWeight: 600}}>I hereby confirm that all information provided is true and correct</span>
                </label>

                {approvedHrOnboardingReq && !instructionAccepted && (
                  <div style={{color: '#ef4444', fontSize: '12px', marginTop: '8px', padding: '8px', background: '#fef2f2', borderRadius: '6px'}}>
                    ⚠ Please open and read the instruction file, then accept it to enable declaration.
                  </div>
                )}

                {!d.declaration && (
                  <div style={{color: '#ef4444', fontSize: '12px', marginTop: '8px', padding: '8px', background: '#fef2f2', borderRadius: '6px'}}>
                    ⚠ You must accept the declaration to submit
                  </div>
                )}
              </div>

              {/* Quick Preview Summary */}
              <div className="ob-section">
                <div className="ob-section-title">📋 Summary</div>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px',
                  background: '#f8fafc',
                  borderRadius: '8px'
                }}>
                  <div>
                    <span style={{fontWeight: 600, fontSize: '13px'}}>Total Fields Completed:</span>
                    <span style={{marginLeft: '8px', color: '#4338ca', fontWeight: 700}}>
                      {Object.values(d).filter(v => v && v !== "" && v !== null && v !== false).length} / 45+
                    </span>
                  </div>
                  <button 
                    className="ob-btn" 
                    onClick={() => setShowPreview(true)}
                    disabled={!d.declaration}
                  >
                    View Full Preview →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* PREVIEW */}
          {showPreview && (
            <div className="ob-card">
              <div className="ob-card-title">Preview — Complete Application</div>
              <div className="ob-preview">
                {/* Personal & ID */}
                <div className="ob-preview-sec">
                  <div className="ob-preview-sec-title">👤 Personal & ID Details</div>
                  <div className="ob-preview-row"><span>Name:</span><span>{d.fullName || "—"}</span></div>
                  <div className="ob-preview-row"><span>DOB:</span><span>{d.dob || "—"}</span></div>
                  <div className="ob-preview-row"><span>Blood Group:</span><span>{d.bloodGroup || "—"}</span></div>
                  <div className="ob-preview-row"><span>Gender:</span><span>{d.gender || "—"}</span></div>
                  <div className="ob-preview-row"><span>Phone:</span><span>{d.phone || "—"}</span></div>
                  <div className="ob-preview-row"><span>Email:</span><span>{d.email || "—"}</span></div>
                  <div className="ob-preview-row"><span>PAN:</span><span>{d.panNumber || "—"}</span></div>
                  {d.passportNumber && <div className="ob-preview-row"><span>Passport:</span><span>{d.passportNumber}</span></div>}
                  {d.drivingLicenseNumber && <div className="ob-preview-row"><span>Driving License:</span><span>{d.drivingLicenseNumber}</span></div>}
                </div>

                {/* Address */}
                <div className="ob-preview-sec">
                  <div className="ob-preview-sec-title">🏠 Address</div>
                  <div className="ob-preview-row"><span>Current:</span><span>{[d.currentAddress, d.currentCity, d.currentState, d.currentPincode].filter(Boolean).join(", ") || "—"}</span></div>
                  <div className="ob-preview-row"><span>Permanent:</span><span>{sameAsCurrent ? "Same as Current" : [d.permanentAddress, d.permanentCity, d.permanentState, d.permanentPincode].filter(Boolean).join(", ") || "—"}</span></div>
                </div>

                {/* Documents */}
                <div className="ob-preview-sec">
                  <div className="ob-preview-sec-title">📄 Documents</div>
                  {d.aadhaarName && <div className="ob-preview-row"><span>Aadhaar:</span><span>✅ {d.aadhaarName}</span></div>}
                  {d.panName && <div className="ob-preview-row"><span>PAN:</span><span>✅ {d.panName}</span></div>}
                  {d.photoName && <div className="ob-preview-row"><span>Photo:</span><span>✅ {d.photoName}</span></div>}
                  {d.passportName && <div className="ob-preview-row"><span>Passport:</span><span>✅ {d.passportName}</span></div>}
                  {d.drivingLicenseName && <div className="ob-preview-row"><span>Driving License:</span><span>✅ {d.drivingLicenseName}</span></div>}
                  {d.collegeIdName && <div className="ob-preview-row"><span>College ID:</span><span>✅ {d.collegeIdName}</span></div>}
                  {d.marksheet10Name && <div className="ob-preview-row"><span>10th:</span><span>✅ {d.marksheet10Name}</span></div>}
                  {d.marksheet12Name && <div className="ob-preview-row"><span>12th:</span><span>✅ {d.marksheet12Name}</span></div>}
                  {(d.certificates || []).filter(c => c.name && c.fileName).map(c => (
                    <div key={c.id} className="ob-preview-row"><span>{c.name}:</span><span>✅ {c.fileName}</span></div>
                  ))}
                </div>

                {/* Education */}
                <div className="ob-preview-sec">
                  <div className="ob-preview-sec-title">🎓 Education</div>
                  {d.education.map((edu, idx) => (
                    <div key={idx} style={{marginBottom: '12px', padding: '8px', background: '#fff', borderRadius: '8px'}}>
                      <div style={{fontWeight: 600, color: '#4338ca'}}>{edu.degree || "—"} {edu.specialization && `- ${edu.specialization}`}</div>
                      <div style={{fontSize: '11px', marginTop: '4px'}}>
                        <div>{edu.institution || "—"}</div>
                        <div style={{display: 'flex', justifyContent: 'space-between', marginTop: '4px'}}>
                          <span>{edu.startMonth || ""} {edu.startYear || ""} - {edu.isPursuing ? 'Present' : `${edu.endMonth || ""} ${edu.endYear || ""}`}</span>
                          <span style={{fontWeight: 600}}>{edu.score || "—"} {edu.scoreType}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bank & Joining */}
                <div className="ob-preview-sec">
                  <div className="ob-preview-sec-title">🏦 Bank & Joining</div>
                  <div className="ob-preview-row"><span>Bank:</span><span>{d.bankName || "—"}</span></div>
                  <div className="ob-preview-row"><span>Account:</span><span>{d.accountNo || "—"}</span></div>
                  <div className="ob-preview-row"><span>IFSC:</span><span>{d.ifsc || "—"}</span></div>
                  <div className="ob-preview-row"><span>Name:</span><span>{d.firstName || ""} {d.lastName || ""}</span></div>
                  <div className="ob-preview-row"><span>Father's Name:</span><span>{d.fatherName || "—"}</span></div>
                  <div className="ob-preview-row"><span>Offer Date:</span><span>{d.offerLetterDate || "—"}</span></div>
                  <div className="ob-preview-row"><span>Accepted Date:</span><span>{d.offerAcceptedDate || "—"}</span></div>
                  {d.expectedJoiningDate && <div className="ob-preview-row"><span>Expected Joining:</span><span>{d.expectedJoiningDate}</span></div>}
                </div>

                {/* Declaration */}
                <div className="ob-preview-sec">
                  <div className="ob-preview-sec-title">📜 Declaration</div>
                  <div className="ob-preview-row"><span>Declared at:</span><span>{d.declarationPlace || "—"}</span></div>
                  <div className="ob-preview-row"><span>Declared on:</span><span>{d.declarationDate}</span></div>
                  <div className="ob-preview-row">
                    <span>Status:</span>
                    <span style={{color: d.declaration ? '#3b82f6' : '#ef4444', fontWeight: 600}}>
                      {d.declaration ? '✓ Accepted' : '✗ Not Accepted'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="ob-actions">
            {step > 0 && !showPreview && (
              <button className="ob-btn-o" onClick={() => setStep(step - 1)}>← Back</button>
            )}
            {showPreview && (
              <button className="ob-btn-o" onClick={() => setShowPreview(false)}>← Edit</button>
            )}
            {!showPreview && step < 5 && (
              <button className="ob-btn" disabled={!canNext()} onClick={() => setStep(step + 1)}>
                Next →
              </button>
            )}
            {!showPreview && step === 5 && (
              <button 
                className="ob-btn" 
                disabled={!d.declaration || !d.declarationPlace || (approvedHrOnboardingReq && (!hrSignFile || !instructionAccepted))} 
                onClick={() => setShowPreview(true)}
              >
                Preview Submission →
              </button>
            )}
            {showPreview && (
              <button 
                className="ob-btn ob-btn-ok" 
                disabled={submitting || !d.declaration} 
                onClick={doSubmit}
              >
                {submitting ? "⏳ Submitting..." : "✓ Confirm & Submit"}
              </button>
            )}
          </div>

          {submitError && (
            <div className="ob-error-box" role="alert" aria-live="assertive">
              ❌ {submitError}
            </div>
          )}

          {showInstructionViewer && approvedHrOnboardingReq && (
            <div onClick={() => setShowInstructionViewer(false)} style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.45)", backdropFilter: "blur(4px)", zIndex: 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: 14, width: "94vw", maxWidth: 980, maxHeight: "88vh", overflow: "hidden", boxShadow: "0 24px 60px -16px rgba(15,23,42,.25)", display: "grid", gridTemplateRows: "auto 1fr auto" }}>
                <div style={{ padding: "14px 16px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>Onboarding Instruction View</div>
                  <button type="button" className="ob-btn-o" onClick={() => setShowInstructionViewer(false)}>Close</button>
                </div>
                <div style={{ padding: 12, overflow: "auto", background: "#f8fafc" }}>
                  {getInstructionDocType() === "pdf" ? (
                    <iframe title="onboarding-instruction" src={`${getInstructionDocUrl()}#toolbar=0&navpanes=0&scrollbar=1`} style={{ width: "100%", minHeight: "62vh", border: "1px solid #dbeafe", borderRadius: 8, background: "#fff" }} />
                  ) : getInstructionDocType() === "image" ? (
                    <img src={getInstructionDocUrl()} alt="Onboarding instruction" style={{ width: "100%", maxHeight: "68vh", objectFit: "contain", border: "1px solid #dbeafe", borderRadius: 8, background: "#fff" }} />
                  ) : (
                    <div style={{ fontSize: 12, color: "#475569", background: "#fff", border: "1px solid #dbeafe", borderRadius: 8, padding: 14 }}>
                      Preview not supported for this file type. Please ask HR to upload PDF/Image instruction file.
                    </div>
                  )}
                </div>
                <div style={{ padding: "12px 16px", borderTop: "1px solid #e2e8f0", background: "#fff" }}>
                  <label className="ob-checkbox-label" style={{ padding: "8px 0" }}>
                    <input
                      type="checkbox"
                      checked={instructionAccepted}
                      onChange={(e) => setInstructionAccepted(e.target.checked)}
                      className="ob-checkbox"
                    />
                    <span className="ob-checkbox-custom"></span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>I have read all the instructions and agree to all terms and conditions of the company</span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingFlow;
